import { useState, useEffect, useCallback } from "react";
import { db } from "../../firebase/config";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore";
import { useStudentAuth } from "./useStudentAuth";

export interface SongbookEntry {
  id: string;
  title: string;
  artist: string;
  content: string;
  capo: number;
  key: string;
  tempo: number;
  /** Duración de cada acorde en beats, en orden de aparición. Si falta, se asume 4 beats cada uno. */
  chordTimings?: number[];
  /** URL de YouTube para sincronización */
  youtubeUrl?: string;
  /** Timestamps en segundos donde ocurre cada cambio de acorde (para sync con video) */
  youtubeTimestamps?: number[];
  createdAt: Timestamp;
}

export interface SongbookInput {
  id?: string;
  title: string;
  artist: string;
  content: string;
  capo: number;
  key: string;
  tempo: number;
  chordTimings?: number[];
  youtubeUrl?: string;
  youtubeTimestamps?: number[];
}

export function useSongbookLibrary() {
  const { user } = useStudentAuth();
  const [items, setItems] = useState<SongbookEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const userId = user?.uid;

  useEffect(() => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }

    const colRef = collection(db, "userSongbooks", userId, "songs");
    const q = query(colRef, orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      const songs: SongbookEntry[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<SongbookEntry, "id">),
      }));
      setItems(songs);
      setLoading(false);
    });

    return unsub;
  }, [userId]);

  const saveSong = useCallback(
    async (input: SongbookInput) => {
      if (!userId) return;
      const { id: inputId, ...data } = input;
      const id = inputId || crypto.randomUUID();
      const ref = doc(db, "userSongbooks", userId, "songs", id);
      // Limpiar undefined — Firestore los rechaza
      const clean = Object.fromEntries(
        Object.entries(data).filter(([, v]) => v !== undefined)
      );
      await setDoc(ref, {
        ...clean,
        createdAt: serverTimestamp(),
      });
      return id;
    },
    [userId]
  );

  const deleteSong = useCallback(
    async (id: string) => {
      if (!userId) return;
      await deleteDoc(doc(db, "userSongbooks", userId, "songs", id));
    },
    [userId]
  );

  return { items, loading, saveSong, deleteSong };
}
